import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router";
import { Button, EmptyState, Icon } from "@xinghai/ui";
import { ApiError } from "../lib/api";

function describe(error: unknown): { title: string; description: string } {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return { title: "页面找不到了", description: "链接可能已经失效，回到首页再试试。" };
    }
    return { title: "页面打不开", description: "请稍后重试，或者回到首页。" };
  }
  if (error instanceof ApiError) {
    return { title: "没能加载内容", description: error.message };
  }
  return { title: "出了点问题", description: "请重试一次；如果一直这样，请联系课程老师。" };
}

/** Route-level error element used by every route in `router.tsx`. */
export function RouteErrorElement() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { title, description } = describe(error);
  return (
    <div className="xz-container">
      <EmptyState
        icon={<Icon id="ui-warning" size={24} />}
        title={title}
        description={description}
        action={
          <div className="xz-row">
            <Button variant="primary" onClick={() => window.location.reload()}>
              重新加载
            </Button>
            <Button variant="secondary" onClick={() => void navigate("/")}>
              回到首页
            </Button>
          </div>
        }
      />
    </div>
  );
}

interface BoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface BoundaryState {
  error: Error | null;
}

/** Last-resort boundary around the whole app (render errors outside routing). */
export class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[xinghai] 未捕获的界面错误", error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div className="xz-container">
        <EmptyState
          icon={<Icon id="ui-warning" size={24} />}
          title="出了点问题"
          description="界面遇到了没有预料到的错误。重新加载一次通常就好了。"
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              重新加载
            </Button>
          }
        />
      </div>
    );
  }
}
